import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Percent } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useSetCryptoOnchainMerchantShare } from '@/lib/api/crypto-onchain-platform';

import { formatPercent, parsePercentInput } from './format';
import type { PlatformMerchant } from './types';

type Props = {
  merchant: PlatformMerchant;
  /** The platform default, for the hint; null while settings are unknown. */
  defaultSharePercent: number | null;
};

/**
 * Per-merchant platform share. "Use the platform default" sends `null`, which
 * clears the override in crypto-api; otherwise a percent with two decimals. The
 * dialog reopens on the merchant's current values, never on an abandoned draft.
 */
export function MerchantShareDialog({ merchant, defaultSharePercent }: Props) {
  const { t, i18n } = useTranslation('manageCryptoPayments');
  const setShare = useSetCryptoOnchainMerchantShare();

  const [isOpen, setIsOpen] = useState(false);
  const [isUsingDefault, setIsUsingDefault] = useState(merchant.sharePercentOverride === null);
  const [percentInput, setPercentInput] = useState(
    String(merchant.sharePercentOverride ?? merchant.effectiveSharePercent),
  );

  const parsedPercent = parsePercentInput(percentInput);
  const isPercentValid = parsedPercent !== null;
  const canSubmit = (isUsingDefault || isPercentValid) && !setShare.isPending;
  const defaultHint =
    defaultSharePercent === null
      ? t('shareDialog.useDefaultHintUnknown')
      : t('shareDialog.useDefaultHint', { percent: formatPercent(defaultSharePercent, i18n.language) });
  const switchId = `use-default-share-${merchant.storeId}`;
  const inputId = `merchant-share-percent-${merchant.storeId}`;

  const handleOpenChange = (nextOpen: boolean) => {
    setIsOpen(nextOpen);
    if (nextOpen) {
      setIsUsingDefault(merchant.sharePercentOverride === null);
      setPercentInput(String(merchant.sharePercentOverride ?? merchant.effectiveSharePercent));
    }
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    setShare.mutate(
      { storeId: merchant.storeId, sharePercent: isUsingDefault ? null : parsedPercent },
      { onSuccess: () => setIsOpen(false) },
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Percent className="h-3.5 w-3.5" />
          {t('merchants.setShare')}
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('shareDialog.title', { store: merchant.storeName })}</DialogTitle>
          <DialogDescription>{t('shareDialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3 rounded-md border p-3">
            <div className="space-y-1">
              <Label htmlFor={switchId}>{t('shareDialog.useDefault')}</Label>
              <p className="text-muted-foreground text-xs">{defaultHint}</p>
            </div>
            <Switch id={switchId} checked={isUsingDefault} onCheckedChange={setIsUsingDefault} />
          </div>

          {!isUsingDefault && (
            <div className="space-y-1.5">
              <Label htmlFor={inputId}>{t('shareDialog.percentLabel')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id={inputId}
                  inputMode="decimal"
                  value={percentInput}
                  onChange={(event) => setPercentInput(event.target.value)}
                  className="w-32 text-right font-mono"
                  aria-invalid={!isPercentValid || undefined}
                  autoFocus
                />
                <span className="text-muted-foreground text-sm">%</span>
              </div>
              {!isPercentValid && <p className="text-destructive text-xs">{t('share.invalid')}</p>}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setIsOpen(false)}>
            {t('actions.cancel')}
          </Button>
          <Button disabled={!canSubmit} onClick={handleSubmit}>
            {setShare.isPending ? t('actions.saving') : t('shareDialog.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
